import React from "react";
import "./index.css";

class ScrollAnimation extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isVisible: false,
    };
    this.domRef = React.createRef();
  }

  componentDidMount() {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        this.setState({ isVisible: true });
        observer.unobserve(this.domRef.current);
      }
    });

    observer.observe(this.domRef.current);
  }

  render() {
    const { children, stagger } = this.props;
    const staggerClass = stagger ? `stagger-${stagger}` : "";

    return (
      <div
        className={`scroll-animation ${
          this.state.isVisible ? "is-visible" : ""
        } ${staggerClass}`}
        ref={this.domRef}
      >
        {children}
      </div>
    );
  }
}

class About extends React.Component {
  handleScroll = (e) => {
    e.preventDefault();
    const href = e.currentTarget.getAttribute("href") || "";
    const targetId = href.replace("#", "");
    const element = document.getElementById(targetId);

    if (element) {
      const yOffset = -80; // Adjust based on navbar height
      const y =
        element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  render() {
    return (
      <section id="about" className="about-section">
        <div className="about-container">
          <ScrollAnimation>
            <div className="about-header">
              <h2 className="about-title">
                About{" "}
                <span className="about-title-highlight">W.L.W. Enterprise</span>
              </h2>
              <div className="about-divider"></div>
              <p className="about-subtitle">
                At W.L.W. Enterprise, we go beyond business—we create community
                impact. Rooted in Youngstown, Ohio, our work spans education,
                behavioral health, childcare, real estate, and event services.
                We are committed to building sustainable partnerships that drive
                empowerment, improve quality of life, and support long-term
                regional growth. Every initiative is tailored to meet the
                evolving needs of the people and communities we serve.
              </p>
            </div>
          </ScrollAnimation>

          {/* Founder Section - Full Width */}
          <ScrollAnimation>
            <div className="founder-section">
              <div className="founder-container">
                <div className="founder-image-container">
                  <img
                    src="https://res.cloudinary.com/dffu1ungl/image/upload/v1749911845/Founder_Image_kwjgts.avif"
                    alt="HaSheen Wilson - Founder"
                    className="founder-image"
                  />
                </div>
                <div className="founder-content">
                  <h3 className="founder-name">HaSheen Wilson</h3>
                  <p className="founder-title">Founder & CEO</p>
                  <div className="founder-bio">
                    <p>
                      WLW Enterprise was founded as a tribute to the life and legacy of Willie Lou Wilson,
                      the beloved mother of our visionary leader. Ms. Wilson was more than a mother—she was
                      a pillar of her community, known for her unwavering compassion, wisdom, and tireless
                      dedication to others. She instilled in her children—and everyone she encountered—a
                      powerful message: “Wherever you go, make sure the people around you know you were there
                      by the positive impact you leave behind.” Ms. Wilson lived those words daily,
                      exemplifying the belief that when people are valued, communities thrive.
                    </p>

                    <p>
                      HaSheen Wilson is a seasoned entrepreneur and educator
                      with over 20 years of leadership experience in diverse
                      sectors. His background spans academia, technology, and
                      business, demonstrating a proven ability to lead and
                      inspire teams towards success.
                    </p>

                    <p>
                      As an entrepreneur, Mr. Wilson founded and successfully managed multiple businesses,
                      including Crawling to Destiny Preschool & Infant Daycare,
                      New Vision Behavioral Health Services, and the Youngstown Event Center.
                      Through these enterprises, he has demonstrated a strong commitment to delivering
                      quality services, creating employment opportunities, and making a lasting positive
                      impact within his community.
                    </p>

                    <p>
                      Driven by a passion for learning and empowerment, Mr. Wilson continues to
                      impact lives through education and advocacy. As a faculty member at Youngstown State University,
                      he mentors aspiring professionals in computer science and communication,
                      inspiring them to lead with purpose and integrity. His commitment extends nationwide through his service
                      on the National Education Association Board, where he champions equal access to quality education for all students.
                    </p>
                
                  </div>
                </div>
              </div>
            </div>
          </ScrollAnimation>

          <div className="about-content">
            <ScrollAnimation>
              <div className="about-text-content">
                {/* Vision & Mission Cards with modern design */}
                <div className="about-cards">
                  <ScrollAnimation stagger={1}>
                    <div className="about-card about-vision-card">
                      {/* Visual elements */}
                      <div className="about-card-icon about-vision-icon">
                        <svg
                          className="about-icon"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      </div>
                      <div className="about-card-decoration"></div>

                      {/* Content */}
                      <h3 className="about-card-title">Our Vision</h3>
                      <p className="about-card-description">
                        To build thriving communities where every individual feels valued, 
                        empowered, and supported.
                      </p>

                      {/* Animation element */}
                      <div className="about-card-indicator"></div>
                    </div>
                  </ScrollAnimation>

                  <ScrollAnimation stagger={2}>
                    <div className="about-card about-mission-card">
                      {/* Visual elements */}
                      <div className="about-card-icon about-mission-icon">
                        <svg
                          className="about-icon"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                      </div>
                      <div className="about-card-decoration"></div>

                      {/* Content */}
                      <h3 className="about-card-title">Our Mission</h3>
                      <p className="about-card-description">
                       Our mission is to enrich lives and strengthen communities through people-first services.
                      </p>

                      {/* Animation element */}
                      <div className="about-card-indicator"></div>
                    </div>
                  </ScrollAnimation>
                </div>
              </div>
            </ScrollAnimation>

            <ScrollAnimation>
              <div className="about-image-grid">
                <div className="about-image-wrapper about-image-1">
                  <img
                    src="Images/Vision-Mission-img1.webp"
                    alt="Team collaboration"
                    className="about-image"
                  />
                </div>
                <div className="about-image-wrapper about-image-2">
                  <img
                    src="Images/Vision-Mission-img2.jpg"
                    alt="Business growth"
                    className="about-image"
                  />
                </div>
                <div className="about-image-wrapper about-image-3">
                  <img
                    src="Images/VM-image3.avif"
                    alt="Business strategy"
                    className="about-image"
                  />
                </div>
                <div className="about-image-wrapper about-image-4">
                  <img
                    src="Images/VM-image4.avif"
                    alt="Team meeting"
                    className="about-image"
                  />
                </div>
              </div>
            </ScrollAnimation>
          </div>

          <ScrollAnimation stagger={3}>
            <div className="logos-section">
              <h3 className="logos-title">Our Businesses</h3>
              <div className="logo-grid">
                <div className="logo-item">
                  <a
                    href="https://crawlingtodestiny.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src="https://res.cloudinary.com/dffu1ungl/image/upload/v1749911844/Crawling_to_Destiny_ukruvw.avif"
                      alt="Crawling to Destiny"
                      className="logo-image"
                    />
                  </a>
                </div>
                <div className="logo-item">
                  <a
                    href="https://nvbhoh.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src="https://res.cloudinary.com/dffu1ungl/image/upload/v1749911844/AV_ftemxw.avif"
                      alt="New Vision Behavioral Health"
                      className="logo-image"
                    />
                  </a>
                </div>

                <div className="logo-item">
                  <a
                    href="https://hitchcockwoodsctr.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src="https://res.cloudinary.com/dffu1ungl/image/upload/v1749911844/Hitchcock_woods_hgvlhj.avif"
                      alt="Hitchcock Woods Preschool & Child Care"
                      className="logo-image"
                    />
                  </a>
                </div>
                <div className="logo-item">
                  <a
                    href="https://youngstowneventcenter.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src="https://res.cloudinary.com/dffu1ungl/image/upload/v1749911845/Youngstown-Event-Center-Logo_nq9fvn.avif"
                      alt="Youngstown Event Center"
                      className="logo-image"
                    />
                  </a>
                </div>
              </div>
            </div>
          </ScrollAnimation>
          <ScrollAnimation stagger={4}>
            <div className="about-cta-container">
              <h3 className="about-cta-title">Ready to grow with us?</h3>
              <a
                href="#services"
                onClick={this.handleScroll}
                className="about-cta-button"
              >
                <span className="about-cta-button-highlight"></span>
                <span className="about-cta-button-overlay"></span>
                <span className="about-cta-button-text">
                  Learn More About W.L.W. Enterprise
                  <svg
                    className="about-cta-button-icon"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </span>
              </a>
            </div>
          </ScrollAnimation>
        </div>
      </section>
    );
  }
}

export default About;
